import React, { useState, useEffect } from 'react';
import { FilmIcon, CheckCircleIcon, PlayIcon, XMarkIcon } from '@heroicons/react/24/outline';

const API_URL = import.meta.env.VITE_API_URL || 'https://groovie-business-api-production.up.railway.app';

export default function ContentLibrary() {
  const [catalog, setCatalog] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectingChannel, setSelectingChannel] = useState(null); // channel being configured

  const token = localStorage.getItem('token');
  const companyId = (() => {
    try { return JSON.parse(atob(token.split('.')[1])).company_id; }
    catch { return null; }
  })();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, chRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/content-library/catalog`, { headers }),
        fetch(`${API_URL}/api/v1/content-library/company/${companyId}/status`, { headers })
      ]);
      setCatalog((await catRes.json()).shows || []);
      setChannels((await chRes.json()).channels || []);
    } catch (e) { console.error('Content library fetch error:', e); }
    setLoading(false);
  };

  const saveShowSelection = async (channelId, showIds) => {
    setSaving(channelId);
    try {
      const res = await fetch(`${API_URL}/api/v1/content-library/channel/${channelId}/shows`, {
        method: 'PUT', headers,
        body: JSON.stringify({ show_ids: showIds })
      });
      if (res.ok) {
        setChannels(prev => prev.map(ch =>
          ch.id === channelId ? { ...ch, selected_shows: showIds, content_library_enabled: showIds.length > 0 } : ch
        ));
      } else {
        alert('Failed to save show selection');
      }
    } catch (e) { alert('Failed to save: ' + e.message); }
    setSaving(null);
  };

  const toggleShow = (channelId, showId) => {
    const ch = channels.find(c => c.id === channelId);
    if (!ch) return;
    const current = ch.selected_shows || [];
    const updated = current.includes(showId)
      ? current.filter(s => s !== showId)
      : [...current, showId];
    saveShowSelection(channelId, updated);
  };

  const selectAll = (channelId) => {
    saveShowSelection(channelId, catalog.map(s => s.id));
  };

  const deselectAll = (channelId) => {
    saveShowSelection(channelId, []);
  };

  const enabledCount = channels.filter(ch => (ch.selected_shows || []).length > 0).length;

  const suitabilityColors = {
    'Retail': 'bg-pink-500/10 text-pink-400 border-pink-500/30',
    'Healthcare': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    'Hospitality': 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    'Office': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    'Fitness': 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    'Entertainment': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-400">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full mx-auto mb-4" />
        Loading content library...
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FilmIcon className="w-7 h-7 text-cyan-400" />
            Content Library
          </h1>
          <p className="text-slate-400 mt-1">Choose which Groovie shows to add to your channels</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl">
          <span className="text-slate-400 text-sm">{enabledCount} of {channels.length} channels with shows</span>
        </div>
      </div>

      {/* Per-Channel Show Selection */}
      <div className="space-y-4">
        {channels.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
            <p className="text-slate-500">No channels found. Create channels first in Channels.</p>
          </div>
        ) : channels.map(ch => {
          const selected = ch.selected_shows || [];
          const isExpanded = selectingChannel === ch.id;
          return (
            <div key={ch.id} className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden">
              {/* Channel Header */}
              <button
                onClick={() => setSelectingChannel(isExpanded ? null : ch.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-slate-750 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selected.length > 0 ? 'bg-cyan-500/20' : 'bg-slate-700'}`}>
                    {selected.length > 0 ? (
                      <CheckCircleIcon className="w-5 h-5 text-cyan-400" />
                    ) : (
                      <PlayIcon className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium">{ch.name}</p>
                    <p className="text-slate-500 text-xs">
                      {selected.length > 0
                        ? `${selected.length} of ${catalog.length} shows selected`
                        : 'No shows selected — tap to configure'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {saving === ch.id && <span className="text-xs text-cyan-400">Saving...</span>}
                  <svg className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded Show Selection */}
              {isExpanded && (
                <div className="border-t border-slate-700 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-slate-400">Select shows for <span className="text-white font-medium">{ch.name}</span></p>
                    <div className="flex gap-2">
                      <button onClick={() => selectAll(ch.id)} className="text-xs text-cyan-400 hover:text-cyan-300 transition">Select All</button>
                      <span className="text-slate-600">|</span>
                      <button onClick={() => deselectAll(ch.id)} className="text-xs text-slate-400 hover:text-white transition">Clear All</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {catalog.map(show => {
                      const isSelected = selected.includes(show.id);
                      return (
                        <button
                          key={show.id}
                          onClick={() => toggleShow(ch.id, show.id)}
                          disabled={saving === ch.id}
                          className={`text-left p-4 rounded-xl border transition-all ${
                            isSelected
                              ? 'bg-cyan-500/10 border-cyan-500/40 ring-1 ring-cyan-500/20'
                              : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-cyan-500/30' : 'bg-slate-700'}`}>
                              {isSelected ? (
                                <CheckCircleIcon className="w-4 h-4 text-cyan-400" />
                              ) : (
                                <PlayIcon className="w-4 h-4 text-slate-500" />
                              )}
                            </div>
                            <span className="text-slate-500 text-[10px]">{show.duration_avg}</span>
                          </div>
                          <h4 className={`font-semibold text-sm mb-1 ${isSelected ? 'text-cyan-300' : 'text-white'}`}>{show.name}</h4>
                          <p className="text-slate-400 text-xs mb-2 line-clamp-2">{show.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {(show.suitable_for || []).map(tag => (
                              <span key={tag} className={`text-[9px] px-1.5 py-0.5 rounded-full border ${suitabilityColors[tag] || 'bg-slate-700/50 text-slate-400 border-slate-600'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
